import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { mfgCatalogApi, mfgCatalogKeys, useMfgUoms, type MfgItemDetail } from "@/lib/mfg-catalog";
import { ApiError } from "@/lib/api";

export function SpineForm({ item }: { item: MfgItemDetail }) {
  const qc = useQueryClient();
  const { data: uoms } = useMfgUoms();
  const [name, setName] = useState(item.name);
  const [itemNumber, setItemNumber] = useState(item.itemNumber ?? "");
  const [description, setDescription] = useState(item.description ?? "");
  const [uom, setUom] = useState(item.unitOfMeasureCode ?? "");

  const save = useMutation({
    mutationFn: () => mfgCatalogApi.update(item.mfgItemId, {
      name, itemNumber: itemNumber || null, description: description || null, unitOfMeasureCode: uom || null,
      material: item.material, waste: item.waste, variant: item.variant, alternative: item.alternative,
    }, item.rowVersionBase64),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mfgCatalogKeys.detail(item.mfgItemId) }); toast.success("Saved"); },
    onError: (e) => toast.error(e instanceof ApiError && e.status === 409 ? "Changed since you opened it — reload." : `Save failed: ${e.message}`),
  });

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Item</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label><Input value={item.itemTypeCode} readOnly disabled /></div>
        <div><Label>Item #</Label><Input value={itemNumber} onChange={(e) => setItemNumber(e.target.value)} /></div>
        <div className="col-span-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="col-span-2"><Label>Description</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div>
          <Label>Unit of measure</Label>
          <select className="h-9 w-full rounded-md border px-2 text-sm" value={uom} onChange={(e) => setUom(e.target.value)}>
            <option value="">—</option>
            {(uoms ?? []).map((u) => <option key={u.mfgUnitOfMeasureCode} value={u.mfgUnitOfMeasureCode}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>Save item</Button>
    </section>
  );
}
